import { useMemo, useCallback } from 'react';
import { useOrganization } from './useOrganization';
import { useUserRoles } from './useUserRoles';

/**
 * Hook to provide organization scoping for queries
 * This ensures data is properly scoped to the user's organization
 */
export const useOrganizationScoping = () => {
  const { organization } = useOrganization();
  const { roles } = useUserRoles();

  // Add defensive check for provider conflicts
  const isValidContext = useMemo(() => {
    return organization !== undefined && roles !== undefined;
  }, [organization, roles]);

  // Users with platform admin access can see all organizations
  // For secure views (no authenticated user), canAccessAllOrganizations should be false
  const canAccessAllOrganizations = roles?.hasAccessToAllOrgs || false;
  
  // Get the organization ID to scope queries to
  const getOrganizationScope = useCallback(() => {
    if (canAccessAllOrganizations) {
      return null; // No scoping needed for super admins
    }
    return organization?.id || null;
  }, [canAccessAllOrganizations, organization?.id]);

  // Build query filters for organization scoping
  const buildOrgFilter = useCallback((query: any) => {
    if (!isValidContext) return query;
    
    // For super admins with access to all orgs, don't apply org filter
    if (canAccessAllOrganizations) {
      console.log('[ORG-SCOPING] Super admin detected, bypassing org filter');
      return query;
    }
    
    const orgId = organization?.id || null;
    if (orgId) {
      console.log('[ORG-SCOPING] Applying org filter:', orgId);
      return query.eq('org_id', orgId);
    }
    console.log('[ORG-SCOPING] No org filter applied');
    return query;
  }, [isValidContext, canAccessAllOrganizations, organization?.id]);

  // Build query filters for quotes with fallback for booking-based filtering
  const buildQuoteFilter = useCallback((query: any) => {
    if (!isValidContext) return query;
    
    // For super admins with access to all orgs, don't apply org filter
    if (canAccessAllOrganizations) {
      console.log('[ORG-SCOPING] Super admin detected, bypassing quote org filter');
      return query;
    }
    
    const orgId = organization?.id || null;
    if (orgId) {
      console.log('[ORG-SCOPING] Applying quote org filter:', orgId);
      // Simply filter by org_id - RLS policies will handle user-level access
      return query.eq('org_id', orgId);
    }
    console.log('[ORG-SCOPING] No quote org filter applied');
    return query;
  }, [isValidContext, canAccessAllOrganizations, organization?.id]);

  // Build query filters for user's bookings with org scoping
  const buildBookingFilter = useCallback((query: any, userId?: string) => {
    if (!isValidContext) return query;
    
    const orgId = organization?.id || null;
    let filteredQuery = query;
    
    if (orgId) {
      filteredQuery = filteredQuery.eq('org_id', orgId);
    }
    
    // For non-admin users, also filter by user_id
    if (!roles?.isAdmin && userId) {
      filteredQuery = filteredQuery.eq('user_id', userId);
    }
    
    return filteredQuery;
  }, [isValidContext, organization?.id, roles?.isAdmin]);

  // Return safe defaults if context is invalid (e.g., during provider conflicts)
  if (!isValidContext) {
    console.warn('[ORG-SCOPING] Invalid context detected, using safe defaults');
    return {
      organizationId: null,
      canAccessAllOrganizations: false,
      getOrganizationScope,
      buildOrgFilter,
      buildQuoteFilter,
      buildBookingFilter,
    };
  }

  return {
    organizationId: organization?.id,
    canAccessAllOrganizations,
    getOrganizationScope,
    buildOrgFilter,
    buildQuoteFilter,
    buildBookingFilter,
  };
};